import { request } from '@/utils/http'

const DEFAULT_GATEWAY_BASE = '/universal'
const RAW_GATEWAY_BASE = import.meta.env.VITE_UNIVERSAL_API_BASE
  || import.meta.env.VITE_APPROVAL_API_BASE
  || DEFAULT_GATEWAY_BASE
const GATEWAY_BASE = RAW_GATEWAY_BASE.endsWith('/') ? RAW_GATEWAY_BASE.slice(0, -1) : RAW_GATEWAY_BASE
const API_BASE = GATEWAY_BASE.endsWith('/api') ? GATEWAY_BASE : `${GATEWAY_BASE}/api`

export const VIEW_SOURCE_INITIATE_PROCESSING = 'initiateProcessing'
export const VIEW_SOURCE_HANDLE_TODO = 'handleTodo'
export const ACTION_APPROVE = 7001
export const ACTION_REJECT = 7002
export const ACTION_UNDO = 7003
export const ACTION_ROLLBACK = 7004
/** 自动同意（与后端 ApprovalWorkflowConstants.ACTION_TYPE_AUTO_APPROVE 一致） */
export const ACTION_AUTO_APPROVE = 7007
export const ACTION_PRESS_TO_DO = 7008
export const ACTION_RETURN = 7006
export const ACTION_CANCEL = 7010
export const ACTION_ADD_SIGNATURE = 7011
export const ACTION_TRANSFER_SIGNATURE = 7012

export interface ApprovalPageResponse<T> {
  pageNum: number
  pageSize: number
  size?: number
  total: number
  pages?: number
  list: T[]
}

export interface ApprovalCondition {
  field: string
  operator?: string
  value: unknown
}

export interface ApprovalMatterListRequest {
  pageNum: number
  pageSize: number
  documentProcessNo?: string
  workflowName?: string
  approveIdList?: Array<string | number>
  documentConfigIdList?: Array<string | number>
  state?: number | null
  isClosed?: boolean | null
  runningState?: number | null
  officeIdList?: Array<string | number>
  applicationDimensions?: number[]
  docNo?: string
  viewSource?: string
  approveByName?: string
  officeName?: string
  sortDesc?: boolean
  queryKey?: string
  conditions?: ApprovalCondition[]
  requireCargoColumndefs?: boolean
  requireWxColumndefs?: boolean
}

export interface ApprovalMatter {
  [key: string]: unknown
  id?: string | number | null
  documentProcessNo?: string | null
  workflowName?: string | null
  createDate?: string | null
  approveId?: string | null
  documentConfigName?: string | null
  documentConfigId?: string | number | null
  creatorId?: string | number | null
  officeId?: string | number | null
  documentProcessId?: string | number | null
  docId?: string | null
  state?: number | null
  stateStr?: string | null
  isClosed?: boolean | null
  docNo?: string | null
  applicationDimension?: number | null
  lastModified?: string | null
  nodeId?: string | number | null
  approveByNameList?: string[] | null
  creatorName?: string | null
  officeName?: string | null
  emailApprovalUrl?: string | null
  consignor?: string | null
  supplierName?: string | null
  businessCreatedAt?: string | null
  etd?: string | null
  eta?: string | null
  atd?: string | null
  ata?: string | null
}

export interface ApprovalMatterCount {
  selfProcessingCount: number
  pendingProcessingCount: number
  processingCount?: number
  completedCount?: number
  undoCount?: number
}

export interface ApprovalActionRequest {
  documentProcessId?: string | number | null
  documentProcessNo?: string | null
  documentConfigId?: string | number | null
  docId?: string | null
  docNo?: string | null
  actionId?: string | number | null
  actionType: number
  officeId?: string | number | null
  remark?: string
  fileList?: ApprovalTraceFile[]
  applicationDimension?: number | null
  signatureUserId?: string | null
  nodeId?: string | number | null
}

export interface ApprovalProcessAction {
  id?: string | number | null
  nodeId?: string | number | null
  actionType?: number | null
  actionName?: string | null
  handlerService?: string | null
  handlerName?: string | null
  handlerActionName?: string | null
  preCriteriaState?: boolean | null
  preCriteriaTip?: string | null
}

export interface ApprovalCheckResult {
  documentProcessId?: string | number | null
  workflowName?: string | null
  actions?: ApprovalProcessAction[] | null
  skipApprove?: boolean | null
  needConfirmPopupOnInitiate?: boolean | null
}

export interface AddSignatureInfo {
  userIds?: Array<string | number>
  fixUserIds?: Array<string | number>
  disableAddSignatureUserIds?: Array<string | number>
}

export interface ApprovalBatchResult {
  errorMsg?: string | null
  formNumber?: string | number | null
}

export interface ApprovalHistoryTrace {
  id?: string | number | null
  documentProcessId?: string | number | null
  documentProcessNodeId?: string | number | null
  processNodeId?: string | number | null
  userId?: string | number | null
  approveByName?: string | null
  creatorName?: string | null
  nodeName?: string | null
  actionType?: number | null
  actionId?: string | number | null
  actionName?: string | null
  remark?: string | null
  runningState?: number | null
  createDate?: string | null
  approveDate?: string | null
  createTime?: string | null
  lastModified?: string | null
  processNode?: {
    name?: string | null
  } | null
  files?: ApprovalTraceFile[] | null
}

export interface ApprovalTraceFile {
  id?: string | number | null
  documentProcessId?: string | number | null
  traceId?: string | number | null
  fileId?: string | null
  fileName?: string | null
  fileSize?: number | null
  createTime?: string | null
  downLoadUrl?: string | null
}

export interface ApprovalProcessHistory {
  documentProcessId?: string | number | null
  workflowId?: string | number | null
  documentConfigId?: string | number | null
  docId?: string | null
  nodeId?: string | number | null
  isClosed?: boolean | null
  remark?: string | null
  runningState?: number | null
  creatorId?: string | number | null
  modifierId?: string | number | null
  createTime?: string | null
  lastModified?: string | null
  traces?: ApprovalHistoryTrace[] | null
  lastProcessNode?: {
    name?: string | null
  } | null
}

export interface ApprovalDocumentProcess {
  id?: string | number | null
  documentProcessNo?: string | null
  docId?: string | null
  docNo?: string | null
  state?: number | null
  runningState?: number | null
  remark?: string | null
  createTime?: string | null
  lastModified?: string | null
}

const normalizePage = <T>(page?: ApprovalPageResponse<T> | null): ApprovalPageResponse<T> => ({
  pageNum: page?.pageNum ?? 1,
  pageSize: page?.pageSize ?? 50,
  size: page?.size ?? 0,
  total: page?.total ?? 0,
  pages: page?.pages ?? 0,
  list: page?.list || []
})

export const listSelfApprovalMatters = async(data: ApprovalMatterListRequest) => {
  const response = await request.post<ApprovalPageResponse<ApprovalMatter>>(`${API_BASE}/manage/self/list`, data)
  return normalizePage(response)
}

export const listPendingApprovalMatters = async(data: ApprovalMatterListRequest) => {
  const response = await request.post<ApprovalPageResponse<ApprovalMatter>>(`${API_BASE}/manage/pending/list`, data)
  return normalizePage(response)
}

export const getApprovalMatterIndexCount = async(documentConfigIds: Array<string | number> = []) => {
  const params = documentConfigIds.length > 0
    ? { documentConfigIds: documentConfigIds.join(',') }
    : undefined
  const response = await request.get<ApprovalMatterCount>(`${API_BASE}/manage/indexCount`, params)
  return {
    selfProcessingCount: response?.selfProcessingCount ?? 0,
    pendingProcessingCount: response?.pendingProcessingCount ?? 0,
    processingCount: response?.processingCount ?? 0,
    completedCount: response?.completedCount ?? 0,
    undoCount: response?.undoCount ?? 0
  }
}

export const getApprovalMatterByDocumentProcessId = (documentProcessId: string | number) => {
  return request.get<ApprovalMatter>(`${API_BASE}/manage/getByDocumentProcessId`, { documentProcessId })
}

export const batchApprovalAction = (data: ApprovalActionRequest[]) => {
  return request.post<ApprovalBatchResult[]>(`${API_BASE}/workflow/actionBatch`, data)
}

export const workflowAction = (data: ApprovalActionRequest) => {
  return request.post(`${API_BASE}/workflow/action`, data)
}

export const batchCancelApproval = (data: ApprovalActionRequest[]) => {
  return request.post<ApprovalBatchResult[]>(`${API_BASE}/workflow/batchCancel`, data)
}

export const workflowCheck = (params: {
  documentProcessId?: string | number | null
  documentConfigId?: string | number | null
  docId?: string | null
  officeId?: string | number | null
  applicationDimension?: number | null
}) => {
  return request.get<ApprovalCheckResult>(`${API_BASE}/workflow/check`, params)
}

export const convertApprovalDocument = (params: {
  documentConfigId: string | number
  docId: string
}) => {
  return request.get<Record<string, unknown>>(`${API_BASE}/workflow/convertDocument`, params)
}

export const getAddSignatureInfo = (documentProcessId: string | number) => {
  return request.get<AddSignatureInfo>(`${API_BASE}/workflow/getAddSignatureInfo`, { documentProcessId })
}

export const getApprovalProcessHistory = (documentProcessId: string | number) => {
  return request.get<ApprovalProcessHistory>(`${API_BASE}/workflow/history`, { documentProcessId })
}

export const getApprovalProcessHistoryByDocId = (params: {
  docId: string
  documentConfigId: string | number
  /** 仅兼容配置迁移后的历史流程查询，默认仍严格按 documentConfigId 匹配。 */
  fallbackToLatest?: boolean
}) => {
  return request.get<ApprovalProcessHistory>(`${API_BASE}/workflow/historyByDocId`, params)
}

export const getApprovalProcessFiles = (documentProcessId: string | number) => {
  return request.get<ApprovalTraceFile[]>(`${API_BASE}/workflow/getFiles`, { documentProcessId })
}

export const getApprovalDocumentProcess = (params: {
  documentProcessId: string | number
  docId: string
  documentConfigId: string | number
}) => {
  return request.get<ApprovalDocumentProcess>(`${API_BASE}/workflow/getDocumentProcess`, params)
}
